/**
 * Primitive colors are the base color palette used in the app.
 * They are not ment to be used directly in the UI, but as a foundation for semantic colors.
 * First colors are the main brand colors based on the brandbook and their variations.
 * Rest of the colors are based on the Figma designs and can be extended.
 */

const primitiveColors = {
  // Brand colors
  brandGreen: '#004F24',
  brandGreenDark: '#002f16',
  brandLime: '#8EBE4E',
  brandLimeDark: '#72983E',
  brandLimeLight: '#F1FBEF',
  brandClaret: '#953640',
  brandClaretLight: '#F7EFF0',
  brandClaretDark: '#592026',
  brandMint: '#E8F6D0',
  brandMintDark: '#A2CB69',
  brandIvory: '#FFE6BA',
  brandIvoryDark: '#ffc872',
  brandBrown: '#A7662B',
  brandBrownDark: '#834a25',
  // Neutral colors
  black: '#262626',
  gray6: '#555555',
  gray5: '#6E6E6E',
  gray4: '#D0D0D0',
  gray3: '#E3E3E3',
  gray2: '#F5F5F5',
  gray1: '#F9F9F9',
  white: '#FFFFFF',
  // Additional colors
  red: '#DD1313',
  redLight: '#FFF0F0',
  yellow: '#F9C615',
  yellowLight: '#FFE6BA',
  blue: '#2B8BBF',
  blueLight: '#EBF3FF',
  purple: '#A053C3',
  transparent: 'transparent',
  blackTransparent05: 'rgba(0, 0, 0, 0.05)',
  blackTransparent60: 'rgba(0, 0, 0, 0.6)',
  whiteTransparent80: 'rgba(255, 255, 255, 0.8)'
} as const

/**
 * Semantic colors are used to convey meaning and status in the UI.
 * They are grouped by their high-level purpose.
 * They should be used directly in the UI components.
 */

export const semanticColors = {
  brand: {
    primary: primitiveColors.brandGreen,
    primaryHover: primitiveColors.brandGreenDark,
    secondary: primitiveColors.brandLime,
    secondaryHover: primitiveColors.brandLimeDark,
    secondaryLight: primitiveColors.brandLimeLight,
    tertiary: primitiveColors.brandClaret,
    tertiaryHover: primitiveColors.brandClaretDark,
    tertiaryLight: primitiveColors.brandClaretLight,
    quaternary: primitiveColors.brandMint,
    quaternaryHover: primitiveColors.brandMintDark,
    quinary: primitiveColors.brandIvory,
    quinaryHover: primitiveColors.brandIvoryDark,
    senary: primitiveColors.brandBrown,
    senaryHover: primitiveColors.brandBrownDark
  },
  background: {
    base: primitiveColors.white
  },
  surface: {
    base: primitiveColors.gray2,
    dark: primitiveColors.gray6,
    hover: primitiveColors.gray1,
    disabled: primitiveColors.gray1,
    transparent: primitiveColors.transparent
  },
  border: {
    base: primitiveColors.gray3,
    baseOnDark: primitiveColors.white,
    strong: primitiveColors.gray4,
    disabled: primitiveColors.gray2,
    focus: primitiveColors.blue
  },
  text: {
    base: primitiveColors.black,
    baseOnDark: primitiveColors.white,
    body: primitiveColors.gray6,
    subtle: primitiveColors.gray5,
    disabled: primitiveColors.gray4,
    link: primitiveColors.brandLime,
    linkHover: primitiveColors.brandLimeDark
  },
  system: {
    success: {
      text: primitiveColors.brandLime,
      background: primitiveColors.white
    },
    error: {
      text: primitiveColors.red,
      background: primitiveColors.redLight
    },
    warning: {
      text: primitiveColors.black,
      background: primitiveColors.yellowLight
    },
    info: {
      text: primitiveColors.brandGreen,
      background: primitiveColors.brandMint
    }
  },
  overlay: {
    dark: primitiveColors.blackTransparent60,
    darkSubtle: primitiveColors.blackTransparent05,
    light: primitiveColors.whiteTransparent80
  },
  additional: {}
} as const

/**
 * Constant components dimensions.
 * Used for proper layout positioning.
 */

export const dimensions = {
  ribbonHeightDesktop: 40,
  ribbonHeightTablet: 40,
  ribbonHeightMobile: 40,
  navHeightDesktop: 175,
  navHeightTablet: 178,
  navHeightMobile: 178,
  navHeightMobileSubmenu: 36,
  navHeightContent: 100,
  topNavMinimalistic: 100,
  authNavHeight: 100,
  supportSessionBarHeight: 48,
  spacingUnit: 4, // px (1 unit = 4px)
  pageMaxWidth: 1600,
  pageMaxWidthCondensed: 1280,
  mainXPadding: 72,
  mainXPaddingMobile: 16
} as const

/**
 * Font family
 */

export const typography = {
  fontFamily: 'var(--poppins), sans-serif'
} as const

/**
 * Spacing system used for defining margins, paddings and gaps.
 */

export const spacingUnits = {
  /** One unit — 4px */
  1: 1 * dimensions.spacingUnit,
  /** 2× Extra small — 8px */
  2: 2 * dimensions.spacingUnit,
  /** Extra small — 12px */
  3: 3 * dimensions.spacingUnit,
  /** Small — 16px */
  4: 4 * dimensions.spacingUnit,
  /** Medium — 24px */
  6: 6 * dimensions.spacingUnit,
  /** Large — 32px */
  8: 8 * dimensions.spacingUnit,
  /** Extra large — 40px */
  10: 10 * dimensions.spacingUnit,
  /** 2× Extra large — 48px */
  12: 12 * dimensions.spacingUnit,
  /** 3× Extra large — 64px */
  16: 16 * dimensions.spacingUnit,
  /** 4× Extra large — 80px */
  20: 20 * dimensions.spacingUnit,
  /** 5× Extra large — 96px */
  24: 24 * dimensions.spacingUnit,
  /** 6× Extra large — 128px */
  32: 32 * dimensions.spacingUnit,
  /** 7× Extra large — 160px */
  40: 40 * dimensions.spacingUnit
  /**
   * Dynamic spacing calculator.
   * @example spacing.get(5) // "20px"
   */
}

export const spacing = {
  /** One unit — 4px */
  1: `${spacingUnits[1]}px`,
  /** 2× Extra small — 8px */
  2: `${spacingUnits[2]}px`,
  /** Extra small — 12px */
  3: `${spacingUnits[3]}px`,
  /** Small — 16px */
  4: `${spacingUnits[4]}px`,
  /** Medium — 24px */
  6: `${spacingUnits[6]}px`,
  /** Large — 32px */
  8: `${spacingUnits[8]}px`,
  /** Extra large — 40px */
  10: `${spacingUnits[10]}px`,
  /** 2× Extra large — 48px */
  12: `${spacingUnits[12]}px`,
  /** 3× Extra large — 64px */
  16: `${spacingUnits[16]}px`,
  /** 4× Extra large — 80px */
  20: `${spacingUnits[20]}px`,
  /** 5× Extra large — 96px */
  24: `${spacingUnits[24]}px`,
  /** 6× Extra large — 128px */
  32: `${spacingUnits[32]}px`,
  /** 7× Extra large — 160px */
  40: `${spacingUnits[40]}px`,

  /**
   * Dynamic spacing calculator.
   * @example spacing.get(5) // "20px"
   */
  get: (units: number) => `${units * dimensions.spacingUnit}px`
} as const

/**
 * Border radius values.
 */

const radius = {
  default: '0',
  small: '4px',
  large: '8px',
  full: '9999px'
} as const

/**
 * Box shadow values used to obtain elevation effects.
 */

const elevation = {
  1: '0 3px 3px -2px rgba(0, 0, 0, 0.20), 0 3px 4px 0 rgba(0, 0, 0, 0.14), 0 1px 8px 0 rgba(0, 0, 0, 0.12)',
  2: '0 5px 5px -3px rgba(0, 0, 0, 0.20), 0 8px 10px 1px rgba(0, 0, 0, 0.14), 0 3px 14px 2px rgba(0, 0, 0, 0.12)'
} as const

export const FontSizeOptions = {
  nano: 'nano',
  micro: 'micro',
  small: 'small',
  base: 'base',
  large: 'large',
  xl: 'xl',
  '2xl': '2xl',
  '3xl': '3xl',
  '4xl': '4xl'
} as const
export type FontSizeOptions = keyof typeof FontSizeOptions

type LineHeightOptions = 100 | 120 | 135 | 150
const lineHeights: Record<LineHeightOptions, number> = {
  100: 1,
  120: 1.2,
  135: 1.35,
  150: 1.5
} as const

const fontSizes: Record<FontSizeOptions, string> = {
  nano: '10px',
  micro: '11px',
  small: '14px',
  base: '16px',
  large: '18px',
  xl: '20px',
  '2xl': '24px',
  '3xl': '30px',
  '4xl': '40px'
} as const

export const theme = {
  colors: semanticColors,
  dimensions,
  radius,
  typography,
  spacing,
  elevation,
  fontSizes
} as const

export const getTextStyle = (
  size: FontSizeOptions,
  lineHeight: LineHeightOptions
) =>
  `font-size: ${fontSizes[size]};
   line-height: ${lineHeights[lineHeight]};
`
